import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Alert,
} from '@mui/material';
import {
  MarkEmailRead,
  MarkEmailUnread,
  Visibility,
  FilterList,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useTranslation } from 'react-i18next';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface Feedback {
  id: number;
  user_id: string;
  category: string;
  title: string;
  content: string;
  created_at: string;
  is_read: boolean;
  read_at: string | null;
  user_email?: string;
}

export default function FeedbackManagement() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');

  useEffect(() => {
    loadFeedbacks();
  }, []);

  const loadFeedbacks = async () => {
    try {
      setLoading(true);

      // 1. feedback 데이터 조회
      const { data: feedbackData, error: feedbackError } = await supabase
        .from('feedback')
        .select('*')
        .order('created_at', { ascending: false });

      if (feedbackError) {
        console.error('Feedback 조회 에러:', feedbackError);
        throw feedbackError;
      }

      // 2. user_id로 profiles에서 email 조회
      const userIds = feedbackData?.map((fb) => fb.user_id).filter(Boolean) || [];
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, email')
        .in('id', userIds);

      // 3. email 매핑
      const profileMap = new Map(profilesData?.map((p) => [p.id, p.email]) || []);

      const formattedData = feedbackData?.map((item: any) => ({
        ...item,
        user_email: profileMap.get(item.user_id) || 'Unknown',
      })) || [];

      console.log('Feedback 데이터:', formattedData);
      setFeedbacks(formattedData);
    } catch (error) {
      console.error('Error loading feedbacks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewFeedback = async (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setDetailDialogOpen(true);

    // Mark as read if not already read
    if (!feedback.is_read) {
      await markAsRead(feedback.id);
    }
  };

  const markAsRead = async (feedbackId: number) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({
          is_read: true,
          read_at: new Date().toISOString(),
        })
        .eq('id', feedbackId);

      if (error) throw error;

      // Update local state
      setFeedbacks((prev) =>
        prev.map((fb) =>
          fb.id === feedbackId
            ? { ...fb, is_read: true, read_at: new Date().toISOString() }
            : fb
        )
      );

      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, is_read: true, read_at: new Date().toISOString() });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAsUnread = async (feedbackId: number) => {
    try {
      const { error } = await supabase
        .from('feedback')
        .update({
          is_read: false,
          read_at: null,
        })
        .eq('id', feedbackId);

      if (error) throw error;

      // Update local state
      setFeedbacks((prev) =>
        prev.map((fb) =>
          fb.id === feedbackId ? { ...fb, is_read: false, read_at: null } : fb
        )
      );

      if (selectedFeedback && selectedFeedback.id === feedbackId) {
        setSelectedFeedback({ ...selectedFeedback, is_read: false, read_at: null });
      }
    } catch (error) {
      console.error('Error marking as unread:', error);
    }
  };

  const filteredFeedbacks = feedbacks.filter((fb) => {
    if (filter === 'unread') return !fb.is_read;
    if (filter === 'read') return fb.is_read;
    return true;
  });

  const unreadCount = feedbacks.filter((fb) => !fb.is_read).length;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          {t('feedback.title')}
        </Typography>
        {unreadCount > 0 && (
          <Alert severity="info">{t('feedback.unreadCount', { count: unreadCount })}</Alert>
        )}
      </Stack>

      <Stack direction="row" spacing={1} mb={2}>
        <Button
          variant={filter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setFilter('all')}
          startIcon={<FilterList />}
        >
          {t('feedback.allFilter', { count: feedbacks.length })}
        </Button>
        <Button
          variant={filter === 'unread' ? 'contained' : 'outlined'}
          onClick={() => setFilter('unread')}
          startIcon={<MarkEmailUnread />}
        >
          {t('feedback.unreadFilter', { count: unreadCount })}
        </Button>
        <Button
          variant={filter === 'read' ? 'contained' : 'outlined'}
          onClick={() => setFilter('read')}
          startIcon={<MarkEmailRead />}
        >
          {t('feedback.readFilter', { count: feedbacks.length - unreadCount })}
        </Button>
      </Stack>

      <TableContainer component={Paper} sx={styles.paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('feedback.statusCol')}</TableCell>
              <TableCell>{t('feedback.categoryCol')}</TableCell>
              <TableCell>{t('feedback.titleCol')}</TableCell>
              <TableCell>{t('feedback.userCol')}</TableCell>
              <TableCell>{t('feedback.writtenAt')}</TableCell>
              <TableCell align="center">{t('feedback.workCol')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredFeedbacks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    {t('feedback.noFeedbackFound')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredFeedbacks.map((feedback) => (
                <TableRow
                  key={feedback.id}
                  sx={{
                    backgroundColor: !feedback.is_read
                      ? isDarkMode
                        ? 'rgba(144, 202, 249, 0.08)'
                        : 'rgba(25, 118, 210, 0.04)'
                      : 'inherit',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <TableCell>
                    <Chip
                      label={feedback.is_read ? t('feedback.read') : t('feedback.unread')}
                      color={feedback.is_read ? 'default' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={feedback.category} variant="outlined" size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      fontWeight={!feedback.is_read ? 'bold' : 'normal'}
                    >
                      {feedback.title}
                    </Typography>
                  </TableCell>
                  <TableCell>{feedback.user_email}</TableCell>
                  <TableCell>
                    {new Date(feedback.created_at).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('feedback.viewDetail')}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewFeedback(feedback)}
                          color="primary"
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {feedback.is_read ? (
                        <Tooltip title={t('feedback.markAsUnread')}>
                          <IconButton
                            size="small"
                            onClick={() => markAsUnread(feedback.id)}
                          >
                            <MarkEmailUnread />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('feedback.markAsReadTooltip')}>
                          <IconButton
                            size="small"
                            onClick={() => markAsRead(feedback.id)}
                          >
                            <MarkEmailRead />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedFeedback && (
          <>
            <DialogTitle>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6">{selectedFeedback.title}</Typography>
                <Chip
                  label={selectedFeedback.is_read ? t('feedback.read') : t('feedback.unread')}
                  color={selectedFeedback.is_read ? 'default' : 'primary'}
                  size="small"
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('feedback.categoryLabel')}
                  </Typography>
                  <Chip label={selectedFeedback.category} variant="outlined" size="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('feedback.userLabel')}
                  </Typography>
                  <Typography variant="body1">{selectedFeedback.user_email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('feedback.writtenAtLabel')}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(selectedFeedback.created_at).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
                {selectedFeedback.read_at && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('feedback.readAtLabel')}
                    </Typography>
                    <Typography variant="body1">
                      {new Date(selectedFeedback.read_at).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('feedback.contentLabel')}
                  </Typography>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    }}
                  >
                    <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedFeedback.content}
                    </Typography>
                  </Paper>
                </Box>
              </Stack>
            </DialogContent>
            <DialogActions>
              {selectedFeedback.is_read ? (
                <Button
                  onClick={() => markAsUnread(selectedFeedback.id)}
                  startIcon={<MarkEmailUnread />}
                >
                  {t('feedback.markAsUnreadButton')}
                </Button>
              ) : (
                <Button
                  onClick={() => markAsRead(selectedFeedback.id)}
                  startIcon={<MarkEmailRead />}
                  variant="contained"
                >
                  {t('feedback.markAsReadButton')}
                </Button>
              )}
              <Button onClick={() => setDetailDialogOpen(false)}>{t("common.close")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
